<template>
  <div class="flex flex-col h-full min-h-0">
    <div class="cm-toolbar shrink-0">
      <h2>UM Board</h2>
      <div class="flex items-center gap-2 ml-4">
        <div class="cm-stat">
          <span class="cm-stat-label">Admitted</span>
          <span class="cm-stat-value">{{ counts.admitted || 0 }}</span>
        </div>
        <div class="cm-stat is-info">
          <span class="cm-stat-label">In Hospital</span>
          <span class="cm-stat-value">{{ counts.working || 0 }}</span>
        </div>
        <div class="cm-stat is-brand">
          <span class="cm-stat-label">Day-30 Arc</span>
          <span class="cm-stat-value">{{ counts.post_discharge || 0 }}</span>
        </div>
        <div class="cm-stat is-warn">
          <span class="cm-stat-label">Alerts</span>
          <span class="cm-stat-value">{{ counts.alerts || 0 }}</span>
        </div>
      </div>
      <div class="flex-1"></div>
      <button
        class="cm-btn cm-btn-sm"
        :class="focusFlagged ? 'cm-btn-primary' : 'cm-btn-secondary'"
        @click="focusFlagged = !focusFlagged"
      >Rounds Focus</button>
      <button class="cm-btn cm-btn-sm cm-btn-secondary" @click="loadBoard">Refresh</button>
    </div>

    <div v-if="todaysCalls.length" class="shrink-0 px-4 py-2.5 border-b border-gray-200 bg-amber-50/50">
      <div class="cm-eyebrow mb-1.5">Today's Calls</div>
      <div class="flex gap-2 overflow-x-auto">
        <button
          v-for="(c, i) in todaysCalls"
          :key="'call' + i"
          class="shrink-0 text-left px-3 py-2 bg-white border border-amber-200 rounded hover:border-amber-400 transition-colors"
          @click="selectPatient(c.patient_identifier)"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-gray-800">{{ titleCase(c.patient_name) }}</span>
            <span v-if="c.readmit_risk != null" class="cm-badge bad">{{ riskPct(c.readmit_risk) }}</span>
          </div>
          <div class="text-[11px] text-gray-500 mt-0.5">{{ reasonLabel(c.reason) }}</div>
        </button>
      </div>
    </div>

    <div class="flex-1 flex min-h-0">
      <div class="w-72 shrink-0 border-r border-gray-200 flex flex-col min-h-0">
        <div class="px-3 py-2 border-b border-gray-100 shrink-0 flex items-center gap-2">
          <span class="cm-eyebrow">Admitted Census</span>
          <input
            v-model="search"
            class="cm-input cm-input-sm flex-1"
            placeholder="Search..."
          />
        </div>
        <div class="flex-1 overflow-y-auto min-h-0">
          <div v-if="loading" class="p-3 space-y-2">
            <div v-for="n in 10" :key="n" class="h-9 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <button
            v-for="row in filteredCensus"
            v-else
            :key="row.patient_identifier"
            class="w-full text-left px-3 py-2 border-b border-gray-100 transition-colors"
            :class="selectedMbi === row.patient_identifier ? 'bg-primary-50' : 'hover:bg-gray-50'"
            @click="selectPatient(row.patient_identifier)"
          >
            <div class="flex items-center gap-1.5">
              <span
                class="w-1.5 h-1.5 rounded-full shrink-0"
                :class="row.work_id ? 'bg-green-500' : 'bg-gray-300'"
              ></span>
              <span class="text-xs font-medium text-gray-800 truncate">{{ titleCase(row.patient_name) || row.patient_identifier }}</span>
              <span v-if="row.readmit_risk != null" class="ml-auto text-[11px] font-semibold" :class="riskColor(row.readmit_risk)">
                {{ riskPct(row.readmit_risk) }}
              </span>
            </div>
            <div class="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1.5">
              <span class="truncate">{{ row.facility || '—' }}</span>
              <span v-if="row.is_readmission" class="cm-badge bad">30d</span>
            </div>
          </button>
          <div v-if="!loading && !filteredCensus.length" class="cm-empty">
            <div class="msg">No admitted patients.</div>
          </div>
        </div>
      </div>

      <div class="flex-1 min-w-0 overflow-y-auto min-h-0 p-4 space-y-5">
        <div>
          <div class="cm-eyebrow mb-2">In Hospital ({{ laneWorking.length }})</div>
          <div class="grid grid-cols-2 xl:grid-cols-3 gap-2.5">
            <button
              v-for="card in laneWorking"
              :key="card.work_id"
              class="text-left border rounded p-3 bg-white transition-colors"
              :class="cardClass(card)"
              @click="selectPatient(card.patient_identifier)"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-800 truncate">{{ titleCase(card.patient_name) }}</span>
                <span v-if="card.readmit_risk != null" class="ml-auto text-xs font-semibold" :class="riskColor(card.readmit_risk)">
                  {{ riskPct(card.readmit_risk) }}
                </span>
              </div>
              <div class="text-[11px] text-gray-500 mt-0.5 truncate">
                {{ card.facility || '—' }} · day {{ (card.card_age_days ?? 0) + 1 }}
              </div>
              <div class="flex items-center gap-1 mt-2">
                <span
                  v-for="m in milestoneDefs"
                  :key="m.key"
                  class="w-2 h-2 rounded-full"
                  :class="card.milestones[m.key] ? 'bg-green-500' : 'bg-gray-200'"
                  :title="m.label"
                ></span>
                <span v-for="a in card.alerts" :key="a" class="cm-badge warn ml-auto">{{ reasonLabel(a) }}</span>
              </div>
            </button>
          </div>
          <div v-if="!laneWorking.length" class="text-xs text-gray-400 px-1 py-3">
            No cases being worked in hospital. Pick a patient from the census and Start Working.
          </div>
        </div>

        <div>
          <div class="cm-eyebrow mb-2">Discharged — Day-30 Arc ({{ lanePostDischarge.length }})</div>
          <div class="grid grid-cols-2 xl:grid-cols-3 gap-2.5">
            <button
              v-for="card in lanePostDischarge"
              :key="card.work_id"
              class="text-left border rounded p-3 bg-white transition-colors"
              :class="cardClass(card)"
              @click="selectPatient(card.patient_identifier)"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-800 truncate">{{ titleCase(card.patient_name) }}</span>
                <span class="ml-auto text-xs font-semibold text-gray-500">
                  day {{ card.days_since_discharge ?? 0 }}/30
                </span>
              </div>
              <div class="text-[11px] text-gray-500 mt-0.5 truncate">
                discharged {{ shortDate(card.discharged_at) }}
              </div>
              <div class="flex items-center gap-1 mt-2">
                <span
                  class="text-[11px]"
                  :class="card.milestones.followup_date ? 'text-green-600' : 'text-red-600 font-medium'"
                >
                  {{ card.milestones.followup_date ? `Follow-up ${shortDate(card.milestones.followup_date)}` : 'No follow-up booked' }}
                </span>
                <span v-if="card.alerts.includes('readmitted')" class="cm-badge bad ml-auto">Readmitted</span>
              </div>
            </button>
          </div>
          <div v-if="!lanePostDischarge.length" class="text-xs text-gray-400 px-1 py-3">
            No cases in the post-discharge arc.
          </div>
        </div>
      </div>

      <div v-if="selected" class="w-[460px] shrink-0 border-l border-gray-200 flex flex-col min-h-0 bg-white">
        <div class="cm-toolbar shrink-0">
          <h2 class="truncate">{{ titleCase(selected.patient_name) || selected.patient_identifier }}</h2>
          <div class="flex-1"></div>
          <button class="cm-btn cm-btn-sm cm-btn-secondary" @click="drawerOpen = true">Chart</button>
          <button v-if="selectedCard" class="cm-btn cm-btn-sm cm-btn-secondary" @click="closeCard">Close Card</button>
          <button class="cm-icon-btn" @click="selectedMbi = null">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto min-h-0 p-4 space-y-4">
          <div class="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
            <span><span class="text-gray-400">Facility</span> {{ selected.facility || '—' }}</span>
            <span><span class="text-gray-400">Admitted</span> {{ shortDate(selected.admit_time) }}</span>
            <span v-if="selected.readmit_risk != null">
              <span class="text-gray-400">AI Risk</span>
              <span class="font-semibold ml-1" :class="riskColor(selected.readmit_risk)">{{ riskPct(selected.readmit_risk) }}</span>
            </span>
            <span v-if="selected.is_readmission" class="text-red-600 font-medium">
              Readmission — prior d/c {{ shortDate(selected.prior_discharge_time) }}
            </span>
          </div>

          <button
            v-if="!selectedCard"
            class="cm-btn cm-btn-primary w-full"
            :disabled="starting"
            @click="startWorking"
          >
            <span v-if="starting" class="cm-spinner"></span>
            Start Working This Patient
          </button>

          <template v-else>
            <div class="cm-panel">
              <div class="cm-panel-head"><div class="cm-panel-title">Milestones</div></div>
              <div class="p-3 space-y-2">
                <div v-for="m in milestoneDefs" :key="m.key" class="flex items-center gap-2">
                  <template v-if="m.type === 'stamp'">
                    <button
                      class="w-4 h-4 rounded-full border shrink-0 transition-colors"
                      :class="selectedCard.milestones[m.key] ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300 hover:border-green-400'"
                      @click="toggleMilestone(m.key)"
                    ></button>
                    <span class="text-sm" :class="selectedCard.milestones[m.key] ? 'text-gray-800' : 'text-gray-500'">
                      {{ m.label }}
                    </span>
                    <span v-if="selectedCard.milestones[m.key]" class="text-[11px] text-gray-400 ml-auto">
                      {{ shortDate(selectedCard.milestones[m.key]) }}
                    </span>
                  </template>
                  <template v-else>
                    <span
                      class="w-4 h-4 rounded-full border shrink-0"
                      :class="selectedCard.milestones[m.key] ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300'"
                    ></span>
                    <span class="text-sm" :class="selectedCard.milestones[m.key] ? 'text-gray-800' : 'text-gray-500'">
                      {{ m.label }}
                    </span>
                    <input
                      type="date"
                      class="cm-input cm-input-sm ml-auto w-36"
                      :value="selectedCard.milestones[m.key] || ''"
                      @change="setDateMilestone(m.key, $event.target.value)"
                    />
                  </template>
                </div>
              </div>
            </div>

            <div class="cm-panel">
              <div class="cm-panel-head"><div class="cm-panel-title">Discharge Barriers</div></div>
              <div class="p-3 space-y-1.5">
                <div
                  v-for="(b, i) in selectedCard.barriers"
                  :key="'b' + i"
                  class="flex items-center gap-2 text-sm"
                >
                  <button
                    class="w-3.5 h-3.5 rounded border shrink-0"
                    :class="b.resolved_at ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300'"
                    @click="toggleBarrier(i, !b.resolved_at)"
                  ></button>
                  <span :class="b.resolved_at ? 'line-through text-gray-400' : 'text-gray-700'">{{ b.text }}</span>
                  <button class="cm-icon-btn ml-auto" @click="removeBarrier(i)">
                    <font-awesome-icon :icon="['fas', 'trash-can']" />
                  </button>
                </div>
                <div class="flex items-center gap-2 pt-1">
                  <input
                    v-model="newBarrier"
                    class="cm-input cm-input-sm flex-1"
                    placeholder="Add a barrier..."
                    @keyup.enter="addBarrier"
                  />
                  <button class="cm-btn cm-btn-sm cm-btn-secondary" @click="addBarrier">Add</button>
                </div>
              </div>
            </div>

            <div class="cm-panel">
              <div class="cm-panel-head">
                <div class="cm-panel-title">Admission Brief</div>
                <div class="cm-panel-head-actions">
                  <button
                    class="cm-btn cm-btn-sm cm-btn-secondary"
                    :disabled="generating.admission_assessment || !config.sats?.admission_assessment"
                    @click="generate('admission_assessment')"
                  >
                    <span v-if="generating.admission_assessment" class="cm-spinner"></span>
                    {{ selectedCard.admission_assessment ? 'Regenerate' : 'Generate' }}
                  </button>
                </div>
              </div>
              <div class="p-3 text-sm text-gray-700">
                <div v-if="!selectedCard.admission_assessment" class="text-xs text-gray-400">
                  Generating automatically after Start Working; refresh shortly, or generate now.
                </div>
                <template v-else>
                  <p>{{ selectedCard.admission_assessment.summary }}</p>
                  <div
                    v-for="(c, i) in (selectedCard.admission_assessment.concerns || [])"
                    :key="'c' + i"
                    class="text-xs mt-1 text-amber-700"
                  >
                    <span class="font-medium">{{ c.label }}:</span> {{ c.value }}
                  </div>
                </template>
              </div>
            </div>

            <div class="cm-panel">
              <div class="cm-panel-head">
                <div class="cm-panel-title">Case Packet</div>
                <div class="cm-panel-head-actions">
                  <button
                    class="cm-btn cm-btn-sm cm-btn-secondary"
                    :disabled="generating.case_packet || !config.sats?.case_packet"
                    @click="generate('case_packet')"
                  >
                    <span v-if="generating.case_packet" class="cm-spinner"></span>
                    {{ selectedCard.case_packet ? 'Regenerate' : 'Generate' }}
                  </button>
                </div>
              </div>
              <div class="p-3 text-sm text-gray-700">
                <div v-if="!selectedCard.case_packet" class="text-xs text-gray-400">
                  Generate before rounds — the presentation prep.
                </div>
                <template v-else>
                  <p class="font-medium text-gray-800">{{ selectedCard.case_packet.headline }}</p>
                  <div class="mt-2"><span class="cm-eyebrow">Clinical Course</span>
                    <p class="mt-0.5">{{ selectedCard.case_packet.clinical_course }}</p></div>
                  <div class="mt-2"><span class="cm-eyebrow">Current Status</span>
                    <p class="mt-0.5">{{ selectedCard.case_packet.current_status }}</p></div>
                  <div v-if="(selectedCard.case_packet.barriers || []).length" class="mt-2">
                    <span class="cm-eyebrow">Barriers</span>
                    <div v-for="(b, i) in selectedCard.case_packet.barriers" :key="'pb' + i" class="mt-1.5 border-l-2 border-amber-300 pl-2.5">
                      <div class="font-medium">{{ b.title }}</div>
                      <div class="text-xs text-gray-600">{{ b.detail }}</div>
                      <div class="text-xs text-gray-400 italic">{{ b.evidence }}</div>
                    </div>
                  </div>
                  <div v-if="(selectedCard.case_packet.questions_for_rounds || []).length" class="mt-2">
                    <span class="cm-eyebrow">Questions for Rounds</span>
                    <ul class="list-disc ml-5 mt-0.5">
                      <li v-for="(q, i) in selectedCard.case_packet.questions_for_rounds" :key="'q' + i">{{ q }}</li>
                    </ul>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="selectedCard.is_readmission" class="cm-panel">
              <div class="cm-panel-head">
                <div class="cm-panel-title">Root Cause</div>
                <div class="cm-panel-head-actions">
                  <button
                    class="cm-btn cm-btn-sm cm-btn-secondary"
                    :disabled="generating.root_cause || !config.sats?.root_cause"
                    @click="generate('root_cause')"
                  >
                    <span v-if="generating.root_cause" class="cm-spinner"></span>
                    {{ selectedCard.root_cause ? 'Regenerate' : 'Analyze' }}
                  </button>
                </div>
              </div>
              <div class="p-3 text-sm text-gray-700">
                <div v-if="!selectedCard.root_cause" class="text-xs text-gray-400">
                  Auto-queued on bounce-back detection. Proposals only — confirm in rounds to record.
                </div>
                <template v-else>
                  <p>{{ selectedCard.root_cause.summary }}</p>
                  <div
                    v-for="(cause, i) in (selectedCard.root_cause.candidate_causes || [])"
                    :key="'rc' + i"
                    class="mt-2 border border-gray-200 rounded p-2.5"
                  >
                    <div class="flex items-center gap-2">
                      <span class="cm-badge brand">{{ pathwayLabel(cause.pathway) }}</span>
                      <span class="font-medium">{{ cause.title }}</span>
                      <span class="text-[11px] text-gray-400 ml-auto">{{ Math.round((cause.confidence || 0) * 100) }}%</span>
                    </div>
                    <p class="text-xs text-gray-600 mt-1">{{ cause.narrative }}</p>
                    <div v-for="(ev, j) in (cause.evidence || [])" :key="'ev' + j" class="text-xs text-gray-400 italic mt-0.5">
                      {{ ev.source }}: "{{ ev.quote }}"
                    </div>
                    <button class="cm-btn cm-btn-sm cm-btn-primary mt-1.5" @click="startDecision(cause)">
                      Confirm &amp; Record
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <div class="cm-panel">
              <div class="cm-panel-head">
                <div class="cm-panel-title">Record a Decision</div>
                <div class="cm-panel-head-actions">
                  <button v-if="!decisionOpen" class="cm-btn cm-btn-sm cm-btn-secondary" @click="startDecision(null)">New</button>
                </div>
              </div>
              <div v-if="decisionOpen" class="p-3 space-y-2.5">
                <input v-model="decision.title" class="cm-input w-full" placeholder="Cause / problem" />
                <div class="flex gap-2">
                  <select v-model="decision.pathway" class="cm-select flex-1">
                    <option value="">Pathway —</option>
                    <option v-for="p in (config.pathways || [])" :key="p" :value="p">{{ pathwayLabel(p) }}</option>
                  </select>
                </div>
                <textarea v-model="decision.summary" rows="2" class="cm-input w-full" placeholder="Summary"></textarea>
                <div
                  v-for="(t, i) in decision.todos"
                  :key="'t' + i"
                  class="flex items-center gap-2"
                >
                  <input v-model="t.title" class="cm-input flex-1" placeholder="Decision / to-do" />
                  <select v-model="t.owner_member_id" class="cm-select w-36">
                    <option :value="null">Unassigned</option>
                    <option v-for="m in members" :key="m.id" :value="m.id">{{ m.name }}</option>
                  </select>
                  <button class="cm-icon-btn" @click="decision.todos.splice(i, 1)">
                    <font-awesome-icon :icon="['fas', 'trash-can']" />
                  </button>
                </div>
                <button class="cm-btn cm-btn-sm cm-btn-secondary" @click="decision.todos.push({ title: '', owner_member_id: null })">
                  + Add to-do
                </button>
                <div class="flex items-center gap-2 pt-1">
                  <button class="cm-btn cm-btn-primary" :disabled="savingDecision || !decisionValid" @click="saveDecision">
                    Save to Record
                  </button>
                  <button class="cm-btn cm-btn-secondary" @click="decisionOpen = false">Cancel</button>
                </div>
              </div>
              <div v-else class="p-3 text-xs text-gray-400">
                Decisions become owned to-dos in the care graph and the Decision Ledger.
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      v-if="drawerOpen && selected"
      class="fixed inset-0 z-40 bg-black/20"
      @click.self="drawerOpen = false"
    >
      <div class="absolute right-0 top-0 h-full w-[50%] min-w-[520px] bg-white shadow-xl overflow-auto">
        <Patient_Drawer
          :patient-identifier="selected.patient_identifier"
          :entity-id="selected.entity_id"
          :patient-data="selected"
          :patient-name="selected.patient_name"
          :enabled-tabs="['details', 'utilization', 'programs', 'engagement', 'care_plan', 'care_team']"
          :show-header="true"
          default-tab="details"
          metric-category="Care Management"
          @close="drawerOpen = false"
        />
      </div>
    </div>

    <div
      v-if="message"
      class="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded shadow-lg text-sm text-white"
      :class="message.type === 'error' ? 'bg-red-600' : 'bg-gray-800'"
    >{{ message.text }}</div>
  </div>
</template>

<script>
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';

const PATHWAY_LABELS = {
  med_access: 'Med Access',
  med_adherence: 'Med Adherence',
  no_followup: 'No Follow-Up',
  decompensation: 'Decompensation',
  transport: 'Transport',
  caregiver: 'Caregiver',
  dme: 'DME',
  other: 'Other',
};

const REASON_LABELS = {
  not_engaged_48h: 'Not engaged 48h+',
  no_edd_day3: 'No discharge date',
  no_followup: 'No follow-up booked',
  readmitted: 'Readmitted',
  high_risk_unworked: 'High risk — not worked',
};

const MILESTONE_DEFS = [
  { key: 'engaged_at', label: 'Engaged in hospital', type: 'stamp' },
  { key: 'context_assessed_at', label: 'Context assessed', type: 'stamp' },
  { key: 'edd', label: 'Discharge plan + expected date', type: 'date' },
  { key: 'followup_date', label: 'Follow-up booked (≤7d post-d/c)', type: 'date' },
  { key: 'handed_off_at', label: 'Handed to TOC', type: 'stamp' },
];

export default {
  name: 'UMBoard',
  components: { Patient_Drawer },
  props: {
    config: { type: Object, required: true },
  },
  data() {
    return {
      loading: true,
      census: [],
      cards: [],
      todaysCalls: [],
      counts: {},
      search: '',
      focusFlagged: false,
      selectedMbi: null,
      starting: false,
      newBarrier: '',
      members: [],
      generating: { admission_assessment: false, case_packet: false, root_cause: false },
      drawerOpen: false,
      decisionOpen: false,
      savingDecision: false,
      decision: { title: '', summary: '', pathway: '', todos: [] },
      message: null,
      milestoneDefs: MILESTONE_DEFS,
    };
  },
  computed: {
    filteredCensus() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.census;
      return this.census.filter(r =>
        (r.patient_name || '').toLowerCase().includes(q)
        || (r.patient_identifier || '').toLowerCase().includes(q)
        || (r.facility || '').toLowerCase().includes(q)
      );
    },
    laneWorking() {
      const lane = this.cards.filter(c => c.stage === 'working');
      return this.focusFlagged ? lane.filter(c => c.alerts.length) : lane;
    },
    lanePostDischarge() {
      const lane = this.cards.filter(c => c.stage === 'post_discharge');
      return this.focusFlagged ? lane.filter(c => c.alerts.length) : lane;
    },
    selectedCard() {
      return this.cards.find(c => c.patient_identifier === this.selectedMbi) || null;
    },
    selected() {
      if (!this.selectedMbi) return null;
      const card = this.selectedCard;
      const row = this.census.find(r => r.patient_identifier === this.selectedMbi);
      if (card) {
        return { ...card, entity_id: card.entity_id ?? row?.entity_id, readmit_risk: card.readmit_risk ?? row?.readmit_risk };
      }
      return row || null;
    },
    decisionValid() {
      return this.decision.title.trim()
        && this.decision.todos.some(t => (t.title || '').trim());
    },
  },
  mounted() {
    this.loadBoard();
    this.loadMembers();
  },
  methods: {
    async loadBoard() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get('/api/care-management/um/board/');
        if (data?.success) {
          this.census = data.census || [];
          this.cards = data.cards || [];
          this.todaysCalls = data.todays_calls || [];
          this.counts = data.counts || {};
        }
      } catch (e) {
        console.error('Error loading UM board:', e);
      } finally {
        this.loading = false;
      }
    },
    async loadMembers() {
      try {
        const { data } = await this.$axios.get('/api/team-assignments/summary/');
        this.members = (data || []).map(r => ({ id: r.member_id, name: r.member__name || 'Unknown' }));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },
    selectPatient(mbi) {
      this.selectedMbi = mbi;
      this.decisionOpen = false;
    },
    async startWorking() {
      const row = this.census.find(r => r.patient_identifier === this.selectedMbi);
      if (!row || this.starting) return;
      this.starting = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/um/board/work/', {
          patient_identifier: row.patient_identifier,
          patient_name: row.patient_name,
          admit_time: row.admit_time,
          facility: row.facility,
          episode_type: row.episode_type,
          is_readmission: row.is_readmission,
          prior_discharge_time: row.prior_discharge_time,
          bene_dob: row.patient_dob,
          readmit_risk: row.readmit_risk,
        });
        if (data?.success) {
          this.showMessage('Working this patient — admission brief is generating.');
          await this.loadBoard();
        } else {
          this.showMessage(data?.error || 'Failed to start', 'error');
        }
      } catch (e) {
        console.error('Error starting work:', e);
        this.showMessage(e.response?.data?.error || 'Failed to start', 'error');
      } finally {
        this.starting = false;
      }
    },
    async toggleMilestone(key) {
      const set = !!this.selectedCard.milestones[key];
      await this._milestone(key, set ? null : true);
    },
    async setDateMilestone(key, dateStr) {
      await this._milestone(key, dateStr || null);
    },
    async _milestone(key, value) {
      try {
        const { data } = await this.$axios.post('/api/care-management/um/board/milestone/', {
          work_id: this.selectedCard.work_id, key, value,
        });
        if (data?.success) {
          this.selectedCard.milestones = { ...this.selectedCard.milestones, ...data.milestones };
          await this.loadBoard();
        }
      } catch (e) {
        console.error('Error setting milestone:', e);
        this.showMessage('Failed to update milestone', 'error');
      }
    },
    async addBarrier() {
      const text = this.newBarrier.trim();
      if (!text) return;
      await this._barrier({ text });
      this.newBarrier = '';
    },
    async toggleBarrier(index, resolved) {
      await this._barrier({ index, resolved });
    },
    async removeBarrier(index) {
      await this._barrier({ index, remove: true });
    },
    async _barrier(payload) {
      try {
        const { data } = await this.$axios.post('/api/care-management/um/board/barrier/', {
          work_id: this.selectedCard.work_id, ...payload,
        });
        if (data?.success) this.selectedCard.barriers = data.barriers;
      } catch (e) {
        console.error('Error updating barrier:', e);
        this.showMessage('Failed to update barrier', 'error');
      }
    },
    async closeCard() {
      if (!window.confirm('Close this case off the board?')) return;
      try {
        const { data } = await this.$axios.post('/api/care-management/um/board/close/', {
          work_id: this.selectedCard.work_id,
        });
        if (data?.success) {
          this.selectedMbi = null;
          await this.loadBoard();
        }
      } catch (e) {
        console.error('Error closing card:', e);
        this.showMessage('Failed to close card', 'error');
      }
    },
    async generate(kind) {
      if (!this.selectedCard || this.generating[kind]) return;
      this.generating[kind] = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/um/generate/', {
          work_id: this.selectedCard.work_id,
          kind,
        });
        const result = await this._pollJob(data.job_id);
        if (result.stillRunning) {
          this.showMessage('Still generating — check back shortly.', 'info');
        }
        await this.loadBoard();
      } catch (e) {
        console.error('UM dossier generation failed:', e);
        this.showMessage(e.response?.data?.error || 'Generation failed', 'error');
      } finally {
        this.generating[kind] = false;
      }
    },
    async _pollJob(jobId) {
      const maxAttempts = 150;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const { data } = await this.$axios.get(`/api/jobs/${jobId}/status/`);
        if (data.status === 'completed') return data.result_data || {};
        if (data.status === 'failed') throw new Error(data.error_message || 'Job failed');
      }
      return { stillRunning: true };
    },
    startDecision(cause) {
      this.decision = {
        title: cause?.title || '',
        summary: cause?.narrative || '',
        pathway: cause?.pathway || '',
        todos: [{ title: '', owner_member_id: null }],
      };
      this.decisionOpen = true;
    },
    async saveDecision() {
      if (!this.decisionValid || this.savingDecision) return;
      this.savingDecision = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/um/rounds/decision/', {
          entity_id: this.selected.entity_id,
          presentation_work_id: this.selectedCard?.work_id || null,
          cause: {
            title: this.decision.title.trim(),
            summary: this.decision.summary.trim(),
            pathway: this.decision.pathway || null,
          },
          todos: this.decision.todos
            .filter(t => (t.title || '').trim())
            .map(t => ({ title: t.title.trim(), owner_member_id: t.owner_member_id })),
        });
        if (data?.success) {
          this.showMessage('Decision recorded to the patient record.');
          this.decisionOpen = false;
          await this.loadBoard();
        } else {
          this.showMessage(data?.error || 'Failed to record decision', 'error');
        }
      } catch (e) {
        console.error('Error recording decision:', e);
        this.showMessage(e.response?.data?.error || 'Failed to record decision', 'error');
      } finally {
        this.savingDecision = false;
      }
    },
    cardClass(card) {
      if (this.selectedMbi === card.patient_identifier) return 'border-primary-400 ring-1 ring-primary-200';
      if (card.alerts.length) return 'border-amber-300 hover:border-amber-400';
      return 'border-gray-200 hover:border-gray-300';
    },
    riskPct(v) {
      if (v == null) return '—';
      const n = Number(v);
      return `${Math.round(n <= 1 ? n * 100 : n)}%`;
    },
    riskColor(v) {
      if (v == null) return 'text-gray-400';
      const n = Number(v) <= 1 ? Number(v) * 100 : Number(v);
      if (n >= 50) return 'text-red-600';
      if (n >= 30) return 'text-amber-600';
      return 'text-gray-500';
    },
    pathwayLabel(p) {
      return PATHWAY_LABELS[p] || p || '—';
    },
    reasonLabel(r) {
      return REASON_LABELS[r] || r;
    },
    titleCase(s) {
      return (s || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    shortDate(v) {
      if (!v) return '—';
      const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return String(v);
      return `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}`;
    },
    showMessage(text, type = 'info') {
      this.message = { text, type };
      setTimeout(() => { this.message = null; }, 4000);
    },
  },
};
</script>
